/**
 * 업링크 로그 펼침 — UDP+HEX 원문과 해석값을 나란히 대조한다 (2026-11-04).
 *
 * HEX 원문(헤더 53B + 항목 8B × N)은 사람이 그대로 읽을 수 없다. 그래서 세 칸으로 보여 준다.
 *   ① 원문 바이트 — 필드 · 항목 단위로 색을 나눈 16진 문자열 (받은 그대로)
 *   ② 헤더 해석   — IMEI · 배터리 · 신호 · 현재 온습도 · 단말 시각 … 위치(바이트) · 원문 · 값
 *   ③ 측정 항목   — 항목마다 원문 8B(온도 2 · 습도 2 · 시각 4)와 풀어낸 측정 시각 · 온도 · 습도
 * 한쪽에 마우스를 올리면 다른 쪽의 같은 바이트가 함께 강조된다 — '이 값이 원문 어디서 왔나' 를 바로 확인한다.
 * 바이트 지도는 서버(`_hex_map`)가 디코더와 같은 배치로 만든다. JSON 원문(확인 응답 등)은 원문 + 해석 표만.
 */
import React, { useState } from 'react';
import { Alert, Table, Tooltip, Typography, Button, message } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { gray } from '../theme/tokens';
import { font } from '../theme';
import { fmtDateTime } from '../utils/date';

const { Text } = Typography;
const num: React.CSSProperties = { fontVariantNumeric: 'tabular-nums' };
const mono: React.CSSProperties = { fontFamily: font.mono, fontSize: 11 };

// 헤더 = 청록 계열, 항목 = 인디고 계열. 이웃한 필드끼리 명도를 번갈아 경계가 보이게 한다
const HEAD_BG = ['#ccfbf1', '#99f6e4'];
const ENT_BG = ['#e0e7ff', '#c7d2fe'];
const HL_BG = '#fde047';

const copy = (t: string) => navigator.clipboard?.writeText(t).then(() => message.success('복사했습니다.'), () => message.error('복사에 실패했습니다.'));

const Head: React.FC<{ title: string; extra?: React.ReactNode }> = ({ title, extra }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: gray[600], marginBottom: 4, minHeight: 22 }}>
    <b style={{ color: gray[700] }}>{title}</b>{extra}
  </div>
);

const Swatch: React.FC<{ bg: string; label: string }> = ({ bg, label }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, color: gray[500] }}>
    <span style={{ width: 10, height: 10, borderRadius: 2, background: bg, border: `1px solid ${gray[300]}` }} />{label}
  </span>
);

const UplinkDetail: React.FC<{ r: any }> = ({ r }) => {
  const dc = r.decoded;
  const [hl, setHl] = useState<string | null>(null);   // 'h0'…(헤더 필드) · 'e1'…(항목 번호)
  if (dc?.error) return <Alert type="warning" showIcon message={`해석 실패: ${dc.error}`} description={<pre style={{ ...mono, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{r.raw}</pre>} />;
  const map = dc?.map;

  const summary = dc && (
    <div style={{ fontSize: 12, color: gray[600], marginBottom: 8, display: 'flex', flexWrap: 'wrap', gap: '2px 12px' }}>
      <span><b style={{ color: gray[800] }}>{r.transport?.toUpperCase()} + {dc.format.toUpperCase()}</b> · {r.raw_len || dc.bytes}B
        {map && <> = 헤더 53B + 항목 8B × {map.entries.length}</>}</span>
      <span>측정 <b style={num}>{dc.records.length}</b>건 (정시 로그 {dc.records.filter((x: any) => x.source === 'log').length} · 업로드 시점 {dc.records.filter((x: any) => x.source !== 'log').length})</span>
      <span>배터리 <b style={num}>{dc.battery_v ?? '-'}</b>V · 신호 <b style={num}>{dc.signal_dbm ?? '-'}</b>dBm</span>
      {dc.device_time && <span>단말 시각 {fmtDateTime(dc.device_time)}</span>}
      <span style={{ color: gray[400] }}>저장 {r.n_inserted}건 · 중복 {Math.max(0, r.n_records - r.n_inserted)}건(앞 업로드와 겹친 구간)</span>
    </div>
  );

  if (!map) {   // JSON 원문 — 원문 그대로 + 해석 표
    return (
      <div>
        {summary}
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          {dc && <div style={{ flex: 'none', width: 420 }}>
            <Head title="해석" />
            <Table size="small" pagination={false} rowKey={(x: any) => `${x.ts}${x.source}`} dataSource={dc.records} scroll={{ y: 220 }}
              columns={[
                { title: '측정 시각', dataIndex: 'ts', width: 140, render: (v: string) => <span style={num}>{fmtDateTime(v)}</span> },
                { title: '온도', dataIndex: 'temp_c', width: 70, render: (v: number) => <span style={num}>{v.toFixed(1)}℃</span> },
                { title: '습도', dataIndex: 'humidity', width: 70, render: (v: number) => <span style={num}>{v.toFixed(1)}%</span> },
                { title: '구분', dataIndex: 'source', render: (v: string) => <Text type="secondary" style={{ fontSize: 11 }}>{v === 'log' ? '정시 로그' : '업로드 시점'}</Text> },
              ]} /></div>}
          <div style={{ flex: 1, minWidth: 0 }}>
            <Head title={`원문 (${r.raw_len || r.raw?.length}B)`} extra={<Button size="small" type="text" icon={<CopyOutlined />} onClick={() => copy(r.raw)} />} />
            <pre style={{ ...mono, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all', background: gray[50], padding: 8, borderRadius: 6, maxHeight: 250, overflow: 'auto' }}>{r.raw}</pre>
            {!dc?.records?.length && <Text type="secondary" style={{ fontSize: 11 }}>측정값이 없는 원문(설정 확인 응답 등)입니다. Downklink_Ack 는 Dragino 펌웨어가 JSON 으로만 보낸다.</Text>}
          </div>
        </div>
      </div>
    );
  }

  const pair = (h: string) => h.match(/../g)?.join(' ') ?? h;
  const rowHl = (key: string) => (hl === key ? 'thl-hex-hl' : '');
  return (
    <div onMouseLeave={() => setHl(null)}>
      {summary}
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        {/* ① 원문 바이트 */}
        <div style={{ flex: '1 1 250px', minWidth: 230 }}>
          <Head title="원문 바이트 (받은 그대로)"
            extra={<><Swatch bg={HEAD_BG[0]} label="헤더" /><Swatch bg={ENT_BG[0]} label="측정 항목" />
              <Tooltip title="16진 원문 복사"><Button size="small" type="text" icon={<CopyOutlined />} onClick={() => copy(r.raw)} style={{ marginLeft: 'auto' }} /></Tooltip></>} />
          <div style={{ ...mono, lineHeight: 1.75, background: gray[50], padding: 8, borderRadius: 6, maxHeight: 286, overflow: 'auto', wordBreak: 'normal' }}>
            {map.header.map((f: any, i: number) => (
              <Tooltip key={`h${i}`} title={<span>{f.name} · {f.off}~{f.off + f.len - 1}번째 바이트<br />{f.value}</span>} mouseEnterDelay={0.25}>
                <span onMouseEnter={() => setHl(`h${i}`)}
                  style={{ background: hl === `h${i}` ? HL_BG : HEAD_BG[i % 2], borderRadius: 2, padding: '1px 0', marginRight: 3, cursor: 'default' }}>{pair(f.hex)}</span>
              </Tooltip>))}
            {map.entries.map((e: any) => (
              <span key={`e${e.no}`} onMouseEnter={() => setHl(`e${e.no}`)}
                style={{ background: hl === `e${e.no}` ? HL_BG : ENT_BG[e.no % 2], color: e.empty ? gray[400] : undefined, borderRadius: 2, padding: '1px 0', marginRight: 3, cursor: 'default' }}>{pair(e.hex)}</span>))}
          </div>
        </div>

        {/* ② 헤더 해석 */}
        <div style={{ flex: 'none', width: 350 }}>
          <Head title="헤더 해석 (53B)" />
          <Table size="small" pagination={false} rowKey="off" dataSource={map.header.map((f: any, i: number) => ({ ...f, i }))} scroll={{ y: 260 }}
            rowClassName={(f: any) => rowHl(`h${f.i}`)} onRow={(f: any) => ({ onMouseEnter: () => setHl(`h${f.i}`) })}
            columns={[
              { title: '필드', dataIndex: 'name', width: 92, render: (v: string, f: any) => <span style={{ fontSize: 12 }}>{v}<div style={{ ...num, fontSize: 11, color: gray[400] }}>{f.off}+{f.len}B</div></span> },
              { title: '원문', dataIndex: 'hex', width: 92, render: (v: string) => <span style={{ ...mono, whiteSpace: 'nowrap' }} title={v}>{v.length > 10 ? `${v.slice(0, 10)}…` : v}</span> },
              { title: '값', dataIndex: 'value', render: (v: string) => <span style={{ ...num, fontSize: 12, wordBreak: 'keep-all' }}>{v}</span> },
            ]} />
        </div>

        {/* ③ 측정 항목 */}
        <div style={{ flex: 'none', width: 440 }}>
          <Head title={`측정 항목 (8B × ${map.entries.length})`} extra={<Text type="secondary" style={{ fontSize: 11 }}>온도 2 · 습도 2 · 시각 4</Text>} />
          <Table size="small" pagination={false} rowKey="no" dataSource={map.entries} scroll={{ y: 260 }}
            rowClassName={(e: any) => rowHl(`e${e.no}`)} onRow={(e: any) => ({ onMouseEnter: () => setHl(`e${e.no}`) })}
            columns={[
              { title: '#', dataIndex: 'no', width: 40, render: (v: number) => <span style={{ ...num, color: gray[500], whiteSpace: 'nowrap' }}>{v}</span> },
              { title: '원문 8B', dataIndex: 'hex', width: 136, render: (v: string) => <span style={{ ...mono, whiteSpace: 'nowrap' }}>
                <span title="온도">{v.slice(0, 4)}</span> <span title="습도">{v.slice(4, 8)}</span> <span title="시각(epoch)" style={{ color: gray[500] }}>{v.slice(8)}</span></span> },
              { title: '측정 시각', dataIndex: 'ts', width: 134, render: (v: string | null) => v ? <span style={{ ...num, fontSize: 12, whiteSpace: 'nowrap' }}>{fmtDateTime(v)}</span> : <Text type="secondary" style={{ fontSize: 11 }}>빈 칸</Text> },
              { title: '온도', dataIndex: 'temp_c', width: 58, render: (v: number, e: any) => e.empty ? '-' : <span style={num}>{v.toFixed(1)}℃</span> },
              { title: '습도', dataIndex: 'humidity', render: (v: number, e: any) => e.empty ? '-' : <span style={num}>{v.toFixed(1)}%</span> },
            ]} />
        </div>
      </div>
    </div>
  );
};

export default UplinkDetail;
